import * as React from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from 'lucide-react';
import clsx from 'clsx';

// Simple button variants for pagination
const buttonVariants = ({
  variant = 'ghost',
  size = 'icon',
}: {
  variant?: 'outline' | 'ghost';
  size?: 'default' | 'icon';
}) => {
  const baseStyles =
    'tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-pointer-events-none disabled:tw-opacity-50';
  const variantStyles = {
    outline:
      'tw-border tw-border-[#535D66] tw-bg-transparent tw-text-white hover:tw-bg-[#1c1f23] hover:tw-border-[#4a5056]',
    ghost:
      'tw-text-white hover:tw-bg-[#1c1f23] hover:tw-text-white',
  };
  const sizeStyles = {
    default: 'tw-h-9 tw-px-4 tw-py-2',
    icon: 'tw-h-9 tw-w-9',
  };
  return clsx(baseStyles, variantStyles[variant], sizeStyles[size]);
};

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={clsx('tw-mx-auto tw-flex tw-w-full tw-justify-center', className)}
      {...props}
    />
  );
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="pagination-content"
      className={clsx('tw-flex tw-flex-row tw-items-center tw-gap-1', className)}
      {...props}
    />
  );
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
} & React.ComponentProps<'a'> & {
    size?: 'default' | 'icon';
  };

function PaginationLink({
  className,
  isActive,
  size = 'icon',
  ...props
}: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={clsx(
        buttonVariants({
          variant: isActive ? 'outline' : 'ghost',
          size,
        }),
        className
      )}
      {...props}
    />
  );
}

function PaginationPrevious({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={clsx('tw-gap-1 tw-px-2.5 sm:tw-pl-2.5', className)}
      {...props}
    >
      <ChevronLeftIcon className="tw-h-4 tw-w-4" />
      <span className="tw-hidden sm:tw-inline-block">Previous</span>
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={clsx('tw-gap-1 tw-px-2.5 sm:tw-pr-2.5', className)}
      {...props}
    >
      <span className="tw-hidden sm:tw-inline-block">Next</span>
      <ChevronRightIcon className="tw-h-4 tw-w-4" />
    </PaginationLink>
  );
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={clsx('tw-flex tw-size-9 tw-items-center tw-justify-center', className)}
      {...props}
    >
      <MoreHorizontalIcon className="tw-size-4" />
      <span className="tw-sr-only">More pages</span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};

